// frontend/src/utils/overtimeValidation.utils.ts - FIXED VERSION
import { ValidationStatus, ValidationStatusInfo } from '../services/overtimeValidation.types';

/**
 * 🆕 SAFE DATE CONVERSION HELPER
 * Handles Firestore Timestamps, Date objects, strings, and numbers
 */
function toSafeDate(value: any): Date | null {
  if (!value) return null;
  
  // Already a Date object
  if (value instanceof Date) {
    return value;
  }
  
  // Firestore Timestamp with toDate method
  if (value && typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (e) {
      console.warn('Failed to convert timestamp:', e);
      return null;
    }
  }
  
  // String that can be converted to Date
  if (typeof value === 'string') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Unix timestamp (number)
  if (typeof value === 'number') {
    return new Date(value);
  }
  
  console.warn('Unable to convert to date:', value);
  return null;
}

/**
 * Get validation status from overtime request
 */
export function getValidationStatus(request: any): ValidationStatus {
  const validation = request.attendanceValidation;
  
  if (!validation) {
    return 'pending_validation';
  }
  
  if (!validation.isValidated && validation.validationPending) {
    return 'pending_validation';
  }
  
  if (validation.isValidated) {
    if (validation.absentCount === 0) {
      return 'validated_complete';
    } else if (validation.presentCount === 0) {
      return 'validated_failed';
    } else {
      return 'validated_partial';
    }
  }
  
  return 'pending_validation';
}

/**
 * Get validation status info with colors and messages
 */
export function getValidationStatusInfo(request: any): ValidationStatusInfo {
  const status = getValidationStatus(request);
  const validation = request.attendanceValidation || {};
  
  const presentCount = validation.presentCount || 0;
  const absentCount = validation.absentCount || 0;
  const totalCount = presentCount + absentCount;
  
  switch (status) {
    case 'pending_validation':
      // 🔧 FIXED: Use safe date conversion instead of .toDate()
      const validationDate = toSafeDate(request.willValidateOn) || new Date();
      const validationTime = validationDate.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      return {
        status: 'pending_validation',
        icon: '⏳',
        color: '#3b82f6',
        bgColor: '#dbeafe',
        message: `Validation scheduled for ${validationTime}`,
        presentCount: 0,
        absentCount: 0,
        totalCount: request.employeeIds?.length || 0
      };
      
    case 'validated_complete':
      return {
        status: 'validated_complete',
        icon: '✅',
        color: '#10b981',
        bgColor: '#d1fae5',
        message: `All ${presentCount} employees validated`,
        presentCount,
        absentCount,
        totalCount
      };
      
    case 'validated_partial':
      return {
        status: 'validated_partial',
        icon: '⚠️',
        color: '#f59e0b',
        bgColor: '#fef3c7',
        message: `${presentCount} present, ${absentCount} absent`,
        presentCount,
        absentCount,
        totalCount
      };
      
    case 'validated_failed':
      return {
        status: 'validated_failed',
        icon: '❌',
        color: '#ef4444',
        bgColor: '#fee2e2',
        message: `All ${absentCount} employees absent`,
        presentCount,
        absentCount,
        totalCount
      };
  }
}

/**
 * Check if validation is complete
 */
export function isValidationComplete(request: any): boolean {
  return request.attendanceValidation?.isValidated === true;
}

/**
 * Check if validation is pending
 */
export function isValidationPending(request: any): boolean {
  return request.attendanceValidation?.validationPending === true;
}

/**
 * Get absent employee names
 */
export function getAbsentEmployeeNames(request: any): string[] {
  return request.attendanceValidation?.absentEmployeeNames || [];
}

/**
 * Format validation date
 */
export function formatValidationDate(date: Date): string {
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}